import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";

interface Section { id: string; labelFr: string; labelEn: string }

interface Props {
  titleFr: string;
  titleEn: string;
  taglineFr?: string;
  taglineEn?: string;
  updatedAt: string;
  illustration?: React.ReactNode;
  // Sommaire cliquable affiché en colonne latérale sur desktop (repli naturel en simple
  // défilement sur mobile, pas assez de place pour une colonne à part) — chaque entrée doit
  // correspondre à l'`id` d'un <H2 id="..."> présent dans children, voir plus bas.
  sections?: Section[];
  // Résumé "en bref" affiché avant le corps du texte — pas un raccourci qui dispenserait de
  // lire le reste (toujours suivi du détail complet juste en dessous), plutôt une porte
  // d'entrée pour qui veut d'abord savoir de quoi il retourne, avant de creuser.
  tldrFr?: string[];
  tldrEn?: string[];
  children: (lang: "fr" | "en") => React.ReactNode;
}

// Layout partagé par les pages du Centre des règles OLTODO (confidentialité, mentions légales,
// CGU, règles de la communauté, paiements, cookies) — refonte 2026-09 : chaque page a désormais
// sa propre illustration de couverture, un résumé "en bref", et un sommaire de navigation,
// plutôt qu'un simple empilement de titres/paragraphes. Volontairement indépendant du
// sélecteur de langue à 12 langues de l'app : un document juridique n'a de valeur que dans une
// langue de référence, donc FR/EN seulement ici, avec le FR comme version qui fait foi en cas
// de divergence.
export default function LegalLayout({ titleFr, titleEn, taglineFr, taglineEn, updatedAt, illustration, sections, tldrFr, tldrEn, children }: Props) {
  const navigate = useNavigate();
  const [lang, setLang] = useState<"fr" | "en">("fr");
  const isMobile = useIsMobile();

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const tldr = lang === "fr" ? tldrFr : tldrEn;

  // html/body/#root sont verrouillés à height:100% + overflow:hidden (voir index.css) — chaque
  // page porte donc son propre défilement. Un simple minHeight:100vh ne suffit pas : sans
  // height + overflowY explicites ici, le contenu qui dépasse un écran est tronqué sans aucun
  // moyen de le faire défiler (page "figée" sur sa première vue, jamais de scroll possible).
  return (
    <div style={{ height: "100vh", overflowY: "auto", overflowX: "hidden", backgroundColor: "#FAFAFA" }}>
      <div style={{ backgroundColor: "white", borderBottom: "1px solid #F0F0F0", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
        {/* navigate(-1) et non navigate("/legal") : un vrai retour dans l'historique. Forcer la
            destination poussait une nouvelle entrée à chaque clic (hub -> page -> hub -> page...),
            ce qui empêchait de jamais redescendre jusqu'à Réglages en cliquant "retour" plusieurs
            fois de suite. Le libellé "Centre des règles" reste un repère visuel, pas la
            destination réelle du clic. */}
        <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", color: "#0a0a0a", fontSize: 14, fontWeight: 700, padding: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {lang === "fr" ? "Centre des règles" : "Rules Center"}
        </button>
        <div style={{ display: "flex", gap: 6 }}>
          {(["fr", "en"] as const).map(l => (
            <button
              key={l}
              onClick={() => setLang(l)}
              style={{
                border: "1px solid #E0E0E0", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                backgroundColor: lang === l ? "#0a0a0a" : "white", color: lang === l ? "white" : "#0a0a0a",
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1040, margin: "0 auto", padding: isMobile ? "32px 20px 80px" : "56px 24px 100px", display: "flex", gap: 56 }}>
        <div style={{ flex: 1, minWidth: 0, maxWidth: 680 }}>
          <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "#999", marginBottom: 14 }}>
            {lang === "fr" ? "Conditions générales et règlements" : "Terms and Policies"}
          </p>
          <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, marginBottom: 10, letterSpacing: -0.5 }}>{lang === "fr" ? titleFr : titleEn}</h1>
          {(taglineFr || taglineEn) && (
            <p style={{ fontSize: 16, color: "#555", lineHeight: 1.6, marginBottom: 16, maxWidth: 520 }}>{lang === "fr" ? taglineFr : taglineEn}</p>
          )}
          <p style={{ fontSize: 12.5, color: "#aaa", marginBottom: 28 }}>
            {lang === "fr" ? `Dernière mise à jour, ${updatedAt}` : `Last updated, ${updatedAt}`}
          </p>

          {isMobile && illustration && (
            <div style={{ backgroundColor: "#F5F5F5", borderRadius: 20, padding: 20, marginBottom: 28, maxWidth: 220 }}>{illustration}</div>
          )}

          {tldr && tldr.length > 0 && (
            <div style={{ backgroundColor: "white", border: "1px solid #EFEFEF", borderRadius: 18, padding: "22px 24px", marginBottom: 36 }}>
              <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "#999", marginBottom: 14 }}>
                {lang === "fr" ? "En bref" : "In short"}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
                {tldr.map((t, i) => (
                  <li key={i} style={{ display: "flex", gap: 10, fontSize: 14, lineHeight: 1.6, color: "#333" }}>
                    <span style={{ flexShrink: 0, marginTop: 3, width: 6, height: 6, borderRadius: "50%", backgroundColor: "#0a0a0a" }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isMobile && sections && sections.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }}>
              {sections.map(s => (
                <button key={s.id} onClick={() => scrollTo(s.id)} style={{ border: "1px solid #E5E5E5", backgroundColor: "white", borderRadius: 20, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, color: "#333", cursor: "pointer" }}>
                  {lang === "fr" ? s.labelFr : s.labelEn}
                </button>
              ))}
            </div>
          )}

          <div style={{ fontSize: 15, lineHeight: 1.8, color: "#222" }}>
            {children(lang)}
          </div>
        </div>

        {!isMobile && (illustration || (sections && sections.length > 0)) && (
          <div style={{ width: 240, flexShrink: 0 }}>
            <div style={{ position: "sticky", top: 96 }}>
              {illustration && (
                <div style={{ backgroundColor: "#F5F5F5", borderRadius: 20, padding: 24, marginBottom: 24 }}>{illustration}</div>
              )}
              {sections && sections.length > 0 && (
                <div style={{ backgroundColor: "white", border: "1px solid #EFEFEF", borderRadius: 16, padding: "16px 6px" }}>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "#bbb", padding: "0 16px 10px" }}>
                    {lang === "fr" ? "Sommaire" : "Contents"}
                  </p>
                  {sections.map(s => (
                    <button key={s.id} onClick={() => scrollTo(s.id)} style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", cursor: "pointer", padding: "8px 16px", fontSize: 13, color: "#444", borderRadius: 8 }}>
                      {lang === "fr" ? s.labelFr : s.labelEn}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const H2 = ({ children, id }: { children: React.ReactNode; id?: string }) => (
  <h2 id={id} style={{ fontSize: 20, fontWeight: 800, marginTop: 40, marginBottom: 14, scrollMarginTop: 96 }}>{children}</h2>
);
// Sous-titre à l'intérieur d'une section H2 — utile pour les pages longues (guide d'aide) où un
// thème se découpe en plusieurs sous-questions sans mériter chacune sa propre section de niveau 2.
export const H3 = ({ children }: { children: React.ReactNode }) => (
  <h3 style={{ fontSize: 15.5, fontWeight: 700, marginTop: 22, marginBottom: 8 }}>{children}</h3>
);
export const P = ({ children }: { children: React.ReactNode }) => (
  <p style={{ marginBottom: 14 }}>{children}</p>
);
export const UL = ({ children }: { children: React.ReactNode }) => (
  <ul style={{ marginBottom: 14, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>{children}</ul>
);
// Puce carrée discrète plutôt qu'une puce ronde par défaut du navigateur — cohérent avec les
// petits repères carrés déjà utilisés ailleurs dans l'app (badges de catégorie).
export const LI = ({ children }: { children: React.ReactNode }) => (
  <li style={{ display: "flex", gap: 10, lineHeight: 1.7 }}>
    <span style={{ flexShrink: 0, marginTop: 8, width: 5, height: 5, backgroundColor: "#0a0a0a", opacity: 0.5 }} />
    <span>{children}</span>
  </li>
);
// Encart mis en valeur pour un point important (ex : délai de séquestre, montant d'une
// commission) — fond gris clair, jamais de couleur, cohérent avec le noir/gris/blanc du reste.
export const CALLOUT = ({ children }: { children: React.ReactNode }) => (
  <div style={{ backgroundColor: "#F5F5F5", borderRadius: 14, padding: "16px 20px", margin: "18px 0", fontSize: 14, lineHeight: 1.7, color: "#222" }}>{children}</div>
);
export const PLACEHOLDER = ({ children }: { children: React.ReactNode }) => (
  <span style={{ backgroundColor: "#FFF3CD", padding: "1px 6px", borderRadius: 4, fontWeight: 700, color: "#7A5C00" }}>[{children}]</span>
);
